// marketing.js
import React, { useState, useEffect } from 'react';
import {
  Select,
  Input,
  Button,
  message,
  Steps,
  Row,
  Col,
  Space,
  ConfigProvider,
  Typography,
  Checkbox,
  Collapse,
  Radio,
  ColorPicker,
  Tooltip,
  Upload,
} from 'antd';
import {
  BookmarkIcon,
  PresentationChartBarIcon,
} from '@heroicons/react/24/outline';
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import {
  createMarketingPowerPoint,
  getUserData,
  createMarketingFlyer,
  uploadImage, // Import the new uploadImage function
} from '../api/marketingApi';

const { Option } = Select;
const { Step } = Steps;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// Styled components (unchanged)
const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid #bfdbfe;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }

  @media (max-width: 767px) {
    padding: 1rem;
    border-radius: 10px;
    box-shadow: none;
    border: none;
    margin: 0 1rem;

    &:hover {
      box-shadow: none;
      transform: none;
    }
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    border-radius: 10px !important;
    border: 2px solid #e6e6e6 !important;
    transition: all 0.3s ease;
    padding: 0.5rem 1rem !important;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: #4a90e2 !important;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2) !important;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 10px;
  height: 40px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 767px) {
    width: 100%;
    height: 50px;
    font-size: 16px;
    justify-content: center;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const StyledSteps = styled(Steps)`
  .ant-steps-item-icon {
    background: white;
    border-color: white;
    color: white;
  }

  .ant-steps-item-title {
    font-weight: 600;
  }

  .ant-steps-item-finish .ant-steps-item-icon {
    background: #52c41a;
    border-color: #52c41a;
  }

  .ant-steps-item-finish .ant-steps-icon {
    color: white !important;
  }

  .ant-steps-item-finish .ant-steps-item-tail::after {
    background-color: #52c41a;
  }

  .ant-steps-item-process .ant-steps-item-icon {
    border-color: #4a90e2;
  }

  .ant-steps-item-process .ant-steps-icon {
    color: white !important;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 10px;
  border: 2px solid #e6e6e6;
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
  width: 100%;

  &:hover,
  &:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const InputWrapper = styled.div`
  margin-bottom: 16px;

  @media (max-width: 767px) {
    margin-bottom: 12px;
  }
`;

const SectionSubtitle = styled.h4`
  font-size: 18px;
  color: #4a4a4a;
  margin-bottom: 16px;
  font-weight: 500;

  @media (max-width: 767px) {
    font-size: 16px;
    margin-bottom: 12px;
  }
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 24px;
  background-color: #f0f2f5;
`;

const MobileContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MobileCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 16px;
`;

const MobileStepTitle = styled(Title)`
  font-size: 24px !important;
  margin-bottom: 24px !important;
  color: #1a202c;
`;

const MobileInputGroup = styled.div`
  margin-bottom: 20px;
`;

const MobileLabel = styled(Text)`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #4a5568;
`;

const MobileButton = styled(Button)`
  width: 100%;
  height: 48px;
  font-size: 16px;
  margin-top: 12px;
  border-radius: 8px;
`;

const MobileCollapse = styled(Collapse)`
  background-color: transparent !important;
  border: none !important;

  .ant-collapse-item {
    border-bottom: 1px solid #e2e8f0 !important;
    margin-bottom: 12px;
  }

  .ant-collapse-header {
    padding: 12px 0 !important;
    font-weight: 500;
    color: #2d3748 !important;
  }

  .ant-collapse-content-box {
    padding: 12px 0 !important;
  }
`;

const ServiceScheduleGrid = styled(Row)`
  width: 100%;
  @media (max-width: 767px) {
    display: block;
  }
`;

const CustomNumberInput = ({ value, onChange, placeholder }) => {
  return (
    <StyledInput
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min="1"
    />
  );
};

const scheduleOptions = [
  ...Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })),
  { value: 'other', label: 'Other' },
];

const renderLabel = (label, tooltip) => (
  <div className="flex items-center">
    <span>{label}</span>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 ml-2" />
    </Tooltip>
  </div>
);

// Helper function to ensure URL has correct prefix
const ensureFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('https://plansync-backend-234288438538.us-central1.run.app')) return url;
  return `https://plansync-backend-234288438538.us-central1.run.app/${url.replace(/^\//, '')}`;
};

const MarketingTemplateForm = () => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    materialType: 'presentation', // Changed default to 'presentation' for diversity
    template: '401k_Pro_PPTX_Template_Teal_Black.pptx',
    clientName: '',
    includeServiceSchedule: true,
    numInvestmentReviews: '4',
    customInvestmentReviews: '',
    numInvestmentBenchmarkReports: '4',
    customBenchmarkReports: '',
    numEducationEvents: '2',
    customEducationEvents: '',
    numPlanReviews: '2',
    customPlanReviews: '',
    feeSchedule: [
      { range: '', fee: '' },
      { range: '', fee: '' },
      { range: '', fee: '' },
    ],
    references: [
      {
        company: '',
        employees: '',
        contactNumber: '',
        contactName: '',
        contactPosition: '',
      },
      {
        company: '',
        employees: '',
        contactNumber: '',
        contactName: '',
        contactPosition: '',
      },
    ],
    advisors: [
      {
        name: '',
        contact: '',
        phone: '',
      }
    ],
    companyInfo: {
      name: '',
      address: '',
      website: '',
      phone: '',
      emailAddress: '',
      foundingYear: '',
      officeLocations: '',
      isIndependent: false,
      brokerageAffiliation: '',
      aum: '',
    },
    colorScheme: '#4a90e2', // Default color scheme
    footerText:
      'All securities offered through Accessible AI Investments, Inc. Member FINRA/SIPC',
    logo_url: null, // URL to the company logo
  });

  const [advisorImages, setAdvisorImages] = useState({});
  const [companyLogo, setCompanyLogo] = useState(null);
  const [userid] = useState(localStorage.getItem('userid'));
  const [userData, setUserData] = useState(null);

  // Validate userid is present
  useEffect(() => {
    if (!userid) {
      message.error('User ID not found. Please log in again.');
    }
  }, [userid]);

  // Helper function to fetch images as Blob (No longer needed since we upload images)
  // Removed `fetchImageAsBlob`

  // console.log('userData:', userData);
  // console.log('userid:', userid);

  useEffect(() => {
    if (userid) {
      getUserData(userid)
        .then((data) => {
          // Store the full user data response
          setUserData(data);

          // Map the data to formData only if we have valid data
          if (data) {
            // Map advisors from userData to formData.advisors
            const advisorsFromUserData = [];
            const advisorImagesFromUserData = {};

            // Loop through possible advisors (1-3)
            for (let i = 1; i <= 3; i++) {
              const advisorKey = `advisor${i}`;
              if (data[advisorKey]) {
                const advisorData = data[advisorKey];
                advisorsFromUserData.push({
                  name: advisorData.Name || '',
                  contact: advisorData.Email || '',
                  phone: advisorData.Phone || '',
                });
                if (advisorData['Profile Image']) {
                  advisorImagesFromUserData[i - 1] = `https://plansync-backend-234288438538.us-central1.run.app/${advisorData['Profile Image']}`;
                }
              }
            }

            // Update formData with userData, maintaining existing values as fallback
            setFormData((prev) => ({
              ...prev,
              advisors:
                advisorsFromUserData.length > 0
                  ? advisorsFromUserData
                  : prev.advisors,
              companyInfo: {
                ...prev.companyInfo,
                name: data.company_name || prev.companyInfo.name,
                address: data.company_address || prev.companyInfo.address,
                website: data.company_website || prev.companyInfo.website,
                phone: data.company_phone || prev.companyInfo.phone,
                emailAddress:
                  data.company_email || prev.companyInfo.emailAddress,
                foundingYear:
                  data.founding_year || prev.companyInfo.foundingYear,
                officeLocations:
                  data.office_locations || prev.companyInfo.officeLocations,
                isIndependent:
                  data.independent_advisor !== undefined
                    ? data.independent_advisor
                    : prev.companyInfo.isIndependent,
                brokerageAffiliation:
                  data.brokerage_affiliation ||
                  prev.companyInfo.brokerageAffiliation,
                aum: data.advisor_aum || prev.companyInfo.aum,
              },
              colorScheme: data.color_scheme || prev.colorScheme,
              footerText: data.company_disclaimer || prev.footerText,
              logo_url: data.logo ? ensureFullUrl(data.logo) : prev.logo_url,
            }));

            // console.log("FORM DATA:", formData);
            // console.log("DATA:", data);
            // console.log("LOGO URL:", formData.logo_url);

            // Update images
            setCompanyLogo(
              data.logo ? ensureFullUrl(data.logo) : null
            );
            setAdvisorImages(advisorImagesFromUserData);

            // console.log("ADVISOR IMAGES:", advisorImages);
            // console.log("COMPANY LOGO:", companyLogo);
          }
        })
        .catch((error) => {
          console.error('Error fetching user data:', error);
          message.error('Failed to load user data. Please try again.');
        });
    }
  }, [userid]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (key === 'materialType') {
      setCurrentStep(0);
    }
  };

  const handleFeeScheduleChange = (index, field, value) => {
    const newFeeSchedule = [...formData.feeSchedule];
    newFeeSchedule[index] = { ...newFeeSchedule[index], [field]: value };
    setFormData((prev) => ({ ...prev, feeSchedule: newFeeSchedule }));
  };

  const handleReferenceChange = (index, field, value) => {
    const newReferences = [...formData.references];
    newReferences[index] = { ...newReferences[index], [field]: value };
    setFormData((prev) => ({ ...prev, references: newReferences }));
  };

  /**
   * Handle image uploads by uploading to the server and storing the returned URL.
   * @param {File} file - The image file to upload.
   * @param {string} type - The type of image ('logo' or 'advisor').
   * @param {number|null} advisorIndex - The index of the advisor if type is 'advisor'.
   * @returns {Promise<void>}
   */
  const handleImageUpload = async (file, type, advisorIndex = null) => {
    try {
      const imageUrl = await uploadImage(file); // Upload the image and get URL

      if (type === 'logo') {
        setCompanyLogo(imageUrl);
        setFormData((prev) => ({
          ...prev,
          logo_url: imageUrl,
        }));
      } else if (type === 'advisor') {
        setAdvisorImages((prev) => ({
          ...prev,
          [advisorIndex]: imageUrl,
        }));
      }

      message.success(`${type === 'logo' ? 'Logo' : 'Advisor'} image uploaded successfully.`);
    } catch (error) {
      console.error('Error uploading image:', error);
      message.error(error.message || 'Failed to upload image. Please try again.');
    }
  };

  const handleGenerateMarketingMaterial = async () => {
    // console.log("GENERATING MARKETING MATERIAL");
    // console.log("FORM DATA:", formData);
    setLoading(true);
    try {
      if (formData.materialType === 'flyer') {
        // Validate required data for flyer
        if (!formData.companyInfo?.name) {
          throw new Error('Company name is required');
        }
        if (!formData.advisors?.length) {
          throw new Error('At least one advisor is required');
        }

        // Prepare advisor data with image URLs
        const advisors = formData.advisors.map((advisor, index) => ({
          ...advisor,
          image_url: advisorImages[index] || null,
        }));

        // console.log("ADVISORS ON LINE 536:", advisors);

        // Prepare company info
        const companyInfo = {
          name: formData.companyInfo.name,           // Changed from 'Company Name'
          address: formData.companyInfo.address,
          website: formData.companyInfo.website,
          phone: formData.companyInfo.phone,
          email: formData.companyInfo.emailAddress,  // Changed from emailAddress
          founding_year: formData.companyInfo.foundingYear,
          office_locations: formData.companyInfo.officeLocations,
          is_independent: formData.companyInfo.isIndependent,
          brokerage_affiliation: formData.companyInfo.brokerageAffiliation,
          aum: formData.companyInfo.aum,
        };

        // console.log("COMPANY INFO ON LINE 552:", companyInfo);

        const response = await createMarketingFlyer(
          userid,                    // Make sure userid is defined
          `AAI_flyer.pdf`,
          formData.logo_url,        // Logo URL
          true,                     // show_logo is required
          advisors,                 // Properly formatted advisors array
          companyInfo,              // Properly formatted company info
          formData.colorScheme      // This becomes sidebar_color
        );

        // Create and trigger download
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${formData.clientName}_flyer.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success('Marketing flyer generated and downloaded successfully');
      } else {
        // Validate required data for presentation
        if (!formData.clientName) {
          throw new Error('Client name is required');
        }
        if (!formData.companyInfo?.name) {
          throw new Error('Company name is required');
        }
        if (!formData.advisors?.length) {
          throw new Error('At least one advisor is required');
        }

        // console.log("GENERATING PRESENTATION");
        // console.log("FORM DATA:", formData);
        

        if (!formData.clientName) {
          throw new Error('Client name is required');
        }
        if (!formData.companyInfo?.name) {
          throw new Error('Company name is required');
        }
        if (!formData.advisors?.length) {
          throw new Error('At least one advisor is required');
        }

        // console.log('Generating PowerPoint');
        // console.log('formData.companyInfo:', formData.companyInfo);
        // console.log('formData.logo_url:', formData.logo_url);

        
        // Prepare company info
        const companyInfo = {
          'Company Name': formData.companyInfo.name,
          address: formData.companyInfo.address,
          website: formData.companyInfo.website,
          phone: formData.companyInfo.phone,
          emailAddress: formData.companyInfo.emailAddress,
          foundingYear: formData.companyInfo.foundingYear,
          officeLocations: formData.companyInfo.officeLocations,
          isIndependent: formData.companyInfo.isIndependent,
          brokerageAffiliation: formData.companyInfo.brokerageAffiliation,
          aum: formData.companyInfo.aum,
        };

        // Use the logo URL
        const logoUrl = formData.logo_url; // Already a URL

        // Prepare placeholders (similar to existing logic)
        const placeholders = {
          client_name: formData.clientName,
          footer_text: formData.footerText,
          domain_url: formData.companyInfo.website,
          advisor_company_name: formData.companyInfo.name,
          founding_year: formData.companyInfo.foundingYear,
          office_locations: formData.companyInfo.officeLocations,
          isIndependent: formData.companyInfo.isIndependent
            ? 'Independent Advisor'
            : '',
          brokerage_affiliation: formData.companyInfo.brokerageAffiliation,
          advisor_aum: formData.companyInfo.aum,
          date: new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
          num_investment_reviews:
            formData.numInvestmentReviews === 'other'
              ? formData.customInvestmentReviews
              : formData.numInvestmentReviews,
          num_investment_benchmark_reports:
            formData.numInvestmentBenchmarkReports === 'other'
              ? formData.customBenchmarkReports
              : formData.numInvestmentBenchmarkReports,
          num_education_events:
            formData.numEducationEvents === 'other'
              ? formData.customEducationEvents
              : formData.numEducationEvents,
          num_plan_reviews:
            formData.numPlanReviews === 'other'
              ? formData.customPlanReviews
              : formData.numPlanReviews,
          company_address: formData.companyInfo.address,
          company_phone: formData.companyInfo.phone,
          advisor_email_address: formData.companyInfo.emailAddress,
        };

        // Add fee schedule placeholders
        formData.feeSchedule.forEach((fee, index) => {
          placeholders[`asset_range_${index + 1}`] = fee.range;
          placeholders[`asset_fee_range_${index + 1}`] = fee.fee;
        });

        // Add references placeholders
        formData.references.forEach((ref, index) => {
          const num = index + 1;
          placeholders[`reference_company_${num}`] = ref.company;
          placeholders[`reference_company_${num}_num_employee`] = ref.employees;
          placeholders[`reference_company_${num}_contact_number`] =
            ref.contactNumber;
          placeholders[`reference_company_${num}_contact_name`] = ref.contactName;
          placeholders[`reference_company_${num}_contact_position`] =
            ref.contactPosition;
        });

        const templateName =
          templates.find((t) => t.value === formData.template)?.templateName;
        if (!templateName) {
          throw new Error('Invalid template selected');
        }

        // console.log('Sending request with template:', templateName);

        const response = await createMarketingPowerPoint(
          templateName,
          userid,
          placeholders,
          logoUrl || null // Pass logo URL
        );

        // Verify the response is a Blob and has the correct type
        if (!(response instanceof Blob)) {
          throw new Error('Invalid response format');
        }

        // console.log('Response blob:', {
        //   size: response.size,
        //   type: response.type,
        // });

        // Create and trigger download
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `${formData.clientName}_presentation.pptx`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        message.success(
          'Marketing material generated and downloaded successfully'
        );
      }
    } catch (error) {
      console.error('Error generating marketing material:', error);
      message.error(
        error.message ||
          'Failed to generate marketing material. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    // console.log('Saving form data for future use');
    message.success('Form data saved successfully!');
    // Implement actual save logic as needed
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const handleEdit = (section) => {
    switch (section) {
      case 'materialType':
        setCurrentStep(0);
        break;
      case 'presentationDetails':
        setCurrentStep(1);
        break;
      default:
        setCurrentStep(0);
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0:
        // Only require client name for presentations, not flyers
        if (formData.materialType === 'presentation') {
          return formData.clientName !== '' && formData.materialType !== '';
        }
        // For flyers, only require material type
        return formData.materialType !== '';
      case 1:
        if (formData.materialType === 'presentation') {
          return formData.template !== '';
        } else {
          return formData.colorScheme !== '';
        }
      default:
        return true;
    }
  };

  const handleStepChange = (current) => {
    for (let i = 0; i < current; i++) {
      if (!isStepValid(i)) {
        message.error(
          'Please complete the previous steps before proceeding.'
        );
        return;
      }
    }
    setCurrentStep(current);
  };

  const pageVariants = {
    initial: { opacity: 0, x: 10 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -10 },
  };

  const pageTransition = {
    type: 'tween',
    ease: 'easeInOut',
    duration: 0.3,
  };

  const templates = [
    {
      value: '401k_Pro_PPTX_Template_Teal_Black.pptx',
      label: 'Teal with Black Background',
      templateName: 'Teal Black',
      pdfUrl:
        'https://docs.google.com/presentation/d/1c2-aKzguJIZsuY_ZHcGk-Sk_e8KIOgob/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true',
    },
    {
      value: '401k_Pro_PPTX_Template_Teal_White.pptx',
      label: 'Teal with White Background',
      templateName: 'Teal White',
      pdfUrl:
        'https://docs.google.com/presentation/d/13luIOnD7Kre-hflaAyMN0Q44GO9UHPJb/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true',
    },
    {
      value: '401k_Pro_PPTX_Template_Green_Black.pptx',
      label: 'Green with Black Background',
      templateName: 'Green Black',
      pdfUrl:
        'https://docs.google.com/presentation/d/1WaLbZsGmtyMQGgb8RrkCXFD3C6_pAMR0/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true',
    },
    {
      value: '401k_Pro_PPTX_Template_Green_White.pptx',
      label: 'Green with White Background',
      templateName: 'Green White',
      pdfUrl:
        'https://docs.google.com/presentation/d/15H4muKlhBLvViGTOzVMJMbeNuslQ9XpF/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true',
    },
    {
      value: '401k_Pro_PPTX_Template_Red_Black.pptx',
      label: 'Red with Black Background',
      templateName: 'Red Black',
      pdfUrl:
        'https://docs.google.com/presentation/d/1Q4PhB0GVfQ3bNssJ4e0D_aZCC8ySMUbs/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true',
    },
    {
      value: '401k_Pro_PPTX_Template_Red_White.pptx',
      label: 'Red with White Background',
      templateName: 'Red White',
      pdfUrl:
        'https://docs.google.com/presentation/d/1ts3hmPq5DZ-le0JvNvH4j_o6VqFwWR1C/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true',
    },
    {
      value: '401k_Pro_PPTX_Template_Orange_Black.pptx',
      label: 'Orange with Black Background',
      templateName: 'Orange Black',
      pdfUrl:
        'https://docs.google.com/presentation/d/1w_WJS12QszJzOCR54MtzXCCF39eNYxqj/edit?usp=drive_link&ouid=111485210408989043988&rtpof=true&sd=true',
    },
    {
      value: '401k_Pro_PPTX_Template_Orange_White.pptx',
      label: 'Orange with White Background',
      templateName: 'Orange White',
      pdfUrl:
        'https://docs.google.com/presentation/d/1UiM-mFGIP1HCoyIEGZbYIRgLALNK5x1x/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true',
    },
  ];

  const handleAddFeeSchedule = () => {
    setFormData((prev) => ({
      ...prev,
      feeSchedule: [...prev.feeSchedule, { range: '', fee: '' }],
    }));
  };

  const handleDeleteFeeSchedule = (index) => {
    setFormData((prev) => ({
      ...prev,
      feeSchedule: prev.feeSchedule.filter((_, i) => i !== index),
    }));
  };

  const handleAddReference = () => {
    setFormData((prev) => ({
      ...prev,
      references: [
        ...prev.references,
        {
          company: '',
          employees: '',
          contactNumber: '',
          contactName: '',
          contactPosition: '',
        },
      ],
    }));
  };

  const handleDeleteReference = (index) => {
    setFormData((prev) => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index),
    }));
  };

  const handleCompanyInfoChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      companyInfo: {
        ...prev.companyInfo,
        [field]: value,
      },
    }));
  };
  const steps = [
    {
      title: 'Material Type',
      content: (
        <div>
          <SectionSubtitle>
            Choose Your Marketing Material
          </SectionSubtitle>
          <InputWrapper>
            <label className="block mb-2">
              {renderLabel(
                'Material Type',
                'Select the type of marketing material you want to create'
              )}
            </label>
            <StyledSelect
              value={formData.materialType}
              onChange={(value) => handleChange('materialType', value)}
            >
              <Option value="presentation">Presentation</Option>
              <Option value="flyer">Basic Flyer</Option>
            </StyledSelect>
          </InputWrapper>
          
          {/* Only show Client Name if presentation is selected */}
          {formData.materialType === 'presentation' && (
            <InputWrapper>
              <label className="block mb-2">
                {renderLabel(
                  'Client Name',
                  "Enter the name of the client you're creating this material for"
                )}
              </label>
              <StyledInput
                placeholder="Client Name"
                value={formData.clientName}
                onChange={(e) => handleChange('clientName', e.target.value)}
                className="mb-4"
              />
            </InputWrapper>
          )}
        </div>
      ),
    },
    ...(formData.materialType === 'presentation'
      ? [
          {
            title: 'Presentation Details',
            content: (
              <div className="space-y-6">
                <div>
                  <SectionSubtitle>
                    Choose Your Presentation Style
                  </SectionSubtitle>
                  <InputWrapper>
                    <StyledSelect
                      value={formData.template}
                      onChange={(value) => handleChange('template', value)}
                    >
                      {templates.map((template) => (
                        <Option key={template.value} value={template.value}>
                          {template.label}
                        </Option>
                      ))}
                    </StyledSelect>
                  </InputWrapper>
                  {formData.template && (
                    <div className="mt-2 ml-1">
                      <a
                        href={
                          templates.find((t) => t.value === formData.template)
                            ?.pdfUrl
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                      >
                        Preview{' '}
                        {
                          templates.find((t) => t.value === formData.template)
                            ?.label
                        }{' '}
                        Template
                      </a>
                    </div>
                  )}
                </div>

                <div>
                  <SectionSubtitle>
                    Proposed Advisor Service Schedule for{' '}
                    {formData.clientName || '[Client Name]'}
                  </SectionSubtitle>
                  <div className="mb-4">
                    <Checkbox
                      checked={formData.includeServiceSchedule}
                      onChange={(e) =>
                        handleChange(
                          'includeServiceSchedule',
                          e.target.checked
                        )
                      }
                    >
                      Include Service Schedule
                    </Checkbox>
                  </div>
                  {formData.includeServiceSchedule && (
                    <ServiceScheduleGrid gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <InputWrapper>
                          <label className="block mb-2">
                            {renderLabel(
                              'Investment Reviews',
                              'How many investment reviews you conduct annually'
                            )}
                          </label>
                          {formData.numInvestmentReviews === 'other' ? (
                            <CustomNumberInput
                              value={formData.customInvestmentReviews}
                              onChange={(value) =>
                                handleChange(
                                  'customInvestmentReviews',
                                  value
                                )
                              }
                              placeholder="Enter custom number"
                            />
                          ) : (
                            <StyledSelect
                              placeholder="Number of Investment Reviews"
                              value={formData.numInvestmentReviews}
                              onChange={(value) => {
                                handleChange(
                                  'numInvestmentReviews',
                                  value
                                );
                                if (value !== 'other') {
                                  handleChange(
                                    'customInvestmentReviews',
                                    ''
                                  );
                                }
                              }}
                              defaultValue="4"
                            >
                              {scheduleOptions.map((option) => (
                                <Option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </Option>
                              ))}
                            </StyledSelect>
                          )}
                        </InputWrapper>
                      </Col>
                      <Col xs={24} md={12}>
                        <InputWrapper>
                          <label className="block mb-2">
                            {renderLabel(
                              'Investment Benchmark Reports',
                              'Number of benchmark reports produced yearly'
                            )}
                          </label>
                          {formData.numInvestmentBenchmarkReports ===
                          'other' ? (
                            <CustomNumberInput
                              value={formData.customBenchmarkReports}
                              onChange={(value) =>
                                handleChange(
                                  'customBenchmarkReports',
                                  value
                                )
                              }
                              placeholder="Enter custom number"
                            />
                          ) : (
                            <StyledSelect
                              placeholder="Number of Investment Benchmark Reports"
                              value={
                                formData.numInvestmentBenchmarkReports
                              }
                              onChange={(value) => {
                                handleChange(
                                  'numInvestmentBenchmarkReports',
                                  value
                                );
                                if (value !== 'other') {
                                  handleChange(
                                    'customBenchmarkReports',
                                    ''
                                  );
                                }
                              }}
                              defaultValue="4"
                            >
                              {scheduleOptions.map((option) => (
                                <Option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </Option>
                              ))}
                            </StyledSelect>
                          )}
                        </InputWrapper>
                      </Col>
                      <Col xs={24} md={12}>
                        <InputWrapper>
                          <label className="block mb-2">
                            {renderLabel(
                              'Education Events',
                              'Educational events or seminars held per year'
                            )}
                          </label>
                          {formData.numEducationEvents === 'other' ? (
                            <CustomNumberInput
                              value={formData.customEducationEvents}
                              onChange={(value) =>
                                handleChange(
                                  'customEducationEvents',
                                  value
                                )
                              }
                              placeholder="Enter custom number"
                            />
                          ) : (
                            <StyledSelect
                              placeholder="Number of Education Events"
                              value={formData.numEducationEvents}
                              onChange={(value) => {
                                handleChange(
                                  'numEducationEvents',
                                  value
                                );
                                if (value !== 'other') {
                                  handleChange(
                                    'customEducationEvents',
                                    ''
                                  );
                                }
                              }}
                              defaultValue="2"
                            >
                              {scheduleOptions.map((option) => (
                                <Option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </Option>
                              ))}
                            </StyledSelect>
                          )}
                        </InputWrapper>
                      </Col>
                      <Col xs={24} md={12}>
                        <InputWrapper>
                          <label className="block mb-2">
                            {renderLabel(
                              'Plan Reviews',
                              'How many plan reviews you conduct annually'
                            )}
                          </label>
                          {formData.numPlanReviews === 'other' ? (
                            <CustomNumberInput
                              value={formData.customPlanReviews}
                              onChange={(value) =>
                                handleChange(
                                  'customPlanReviews',
                                  value
                                )
                              }
                              placeholder="Enter custom number"
                            />
                          ) : (
                            <StyledSelect
                              placeholder="Number of Plan Reviews"
                              value={formData.numPlanReviews}
                              onChange={(value) => {
                                handleChange(
                                  'numPlanReviews',
                                  value
                                );
                                if (value !== 'other') {
                                  handleChange(
                                    'customPlanReviews',
                                    ''
                                  );
                                }
                              }}
                              defaultValue="2"
                            >
                              {scheduleOptions.map((option) => (
                                <Option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </Option>
                              ))}
                            </StyledSelect>
                          )}
                        </InputWrapper>
                      </Col>
                    </ServiceScheduleGrid>
                  )}
                </div>

                <div>
                  <SectionSubtitle>
                    {renderLabel(
                      'Fee Structure',
                      'Define your fee structure for different asset ranges'
                    )}
                  </SectionSubtitle>
                  <div className="mb-4">
                    <Checkbox
                      checked={formData.feeSchedule.length > 0}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            feeSchedule: [],
                          }));
                        } else if (formData.feeSchedule.length === 0) {
                          setFormData((prev) => ({
                            ...prev,
                            feeSchedule: [
                              { range: '', fee: '' },
                              { range: '', fee: '' },
                              { range: '', fee: '' },
                            ],
                          }));
                        }
                      }}
                    >
                      Include Fee Schedule
                    </Checkbox>
                  </div>
                  {formData.feeSchedule.length > 0 && (
                    <>
                      {formData.feeSchedule.map((fee, index) => (
                        <div
                          key={index}
                          className="flex space-x-2 items-start"
                        >
                          <InputWrapper className="flex-1">
                            <StyledInput
                              placeholder={`Asset Range ${index + 1}`}
                              value={fee.range}
                              onChange={(e) =>
                                handleFeeScheduleChange(
                                  index,
                                  'range',
                                  e.target.value
                                )
                              }
                              style={{ marginBottom: 8 }}
                            />
                          </InputWrapper>
                          <div className="flex-1 flex items-start space-x-2">
                            <InputWrapper className="flex-grow">
                              <StyledInput
                                placeholder={`Fee for Range ${index + 1}`}
                                value={fee.fee}
                                onChange={(e) =>
                                  handleFeeScheduleChange(
                                    index,
                                    'fee',
                                    e.target.value
                                  )
                                }
                              />
                            </InputWrapper>
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteFeeSchedule(index)}
                              danger
                            />
                          </div>
                        </div>
                      ))}
                      <MobileButton
                        onClick={handleAddFeeSchedule}
                        icon={<PlusOutlined />}
                      >
                        Add Range
                      </MobileButton>
                    </>
                  )}
                </div>

                <div>
                  <SectionSubtitle>
                    {renderLabel(
                      'Client References',
                      'Provide references from existing clients'
                    )}
                  </SectionSubtitle>
                  <div className="mb-4">
                    <Checkbox
                      checked={formData.references.length > 0}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            references: [],
                          }));
                        } else if (formData.references.length === 0) {
                          setFormData((prev) => ({
                            ...prev,
                            references: [
                              {
                                company: '',
                                employees: '',
                                contactNumber: '',
                                contactName: '',
                                contactPosition: '',
                              },
                              {
                                company: '',
                                employees: '',
                                contactNumber: '',
                                contactName: '',
                                contactPosition: '',
                              },
                            ],
                          }));
                        }
                      }}
                    >
                      Include Reference
                    </Checkbox>
                  </div>
                  {formData.references.length > 0 && (
                    <Space
                      direction="vertical"
                      style={{ width: '100%', marginTop: 16 }}
                    >
                      {formData.references.map(
                        (reference, referenceIndex) => (
                          <div key={referenceIndex} className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                              <Text strong>
                                Reference {referenceIndex + 1}
                              </Text>
                              <Button
                                icon={<MinusOutlined />}
                                onClick={() =>
                                  handleDeleteReference(referenceIndex)
                                }
                                danger
                              />
                            </div>
                            <Space
                              direction="vertical"
                              style={{ width: '100%' }}
                            >
                              <StyledInput
                                placeholder="Company Name"
                                value={reference.company}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'company',
                                    e.target.value
                                  )
                                }
                              />
                              <StyledInput
                                placeholder="Number of Employees"
                                type="number"
                                value={reference.employees}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'employees',
                                    e.target.value
                                  )
                                }
                              />
                              <StyledInput
                                placeholder="Contact Number"
                                value={reference.contactNumber}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'contactNumber',
                                    e.target.value
                                  )
                                }
                              />
                              <StyledInput
                                placeholder="Contact Name"
                                value={reference.contactName}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'contactName',
                                    e.target.value
                                  )
                                }
                              />
                              <StyledInput
                                placeholder="Contact Position"
                                value={reference.contactPosition}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'contactPosition',
                                    e.target.value
                                  )
                                }
                              />
                            </Space>
                          </div>
                        )
                      )}
                      <MobileButton
                        onClick={handleAddReference}
                        type="dashed"
                        icon={<PlusOutlined />}
                      >
                        Add Reference
                      </MobileButton>
                    </Space>
                  )}
                </div>

                <div>
                  <SectionSubtitle>Company Details</SectionSubtitle>
                  <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size="large"
                  >
                    <InputWrapper>
                      <label>Company Name</label>
                      <StyledInput
                        value={formData.companyInfo.name}
                        onChange={(e) =>
                          handleCompanyInfoChange('name', e.target.value)
                        }
                        placeholder="Company Name"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <label>Company Address</label>
                      <StyledInput
                        value={formData.companyInfo.address}
                        onChange={(e) =>
                          handleCompanyInfoChange('address', e.target.value)
                        }
                        placeholder="Company Address"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <label>Website</label>
                      <StyledInput
                        value={formData.companyInfo.website}
                        onChange={(e) =>
                          handleCompanyInfoChange('website', e.target.value)
                        }
                        placeholder="Website URL"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <label>Phone Number</label>
                      <StyledInput
                        value={formData.companyInfo.phone}
                        onChange={(e) =>
                          handleCompanyInfoChange('phone', e.target.value)
                        }
                        placeholder="Phone Number"
                      />
                    </InputWrapper>
                    
                    <InputWrapper>
                      <label>Founding Year</label>
                      <StyledInput
                        value={formData.companyInfo.foundingYear}
                        onChange={(e) =>
                          handleCompanyInfoChange('foundingYear', e.target.value)
                        }
                        placeholder="Founding Year"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <label>Office Locations</label>
                      <StyledInput
                        value={formData.companyInfo.officeLocations}
                        onChange={(e) =>
                          handleCompanyInfoChange('officeLocations', e.target.value)
                        }
                        placeholder="Office Locations (e.g., NC, SC)"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <Checkbox
                        checked={formData.companyInfo.isIndependent}
                        onChange={(e) =>
                          handleCompanyInfoChange(
                            'isIndependent',
                            e.target.checked
                          )
                        }
                      >
                        Independent Advisor
                      </Checkbox>
                    </InputWrapper>

                    <InputWrapper>
                      <label>Brokerage Affiliation</label>
                      <StyledInput
                        value={formData.companyInfo.brokerageAffiliation}
                        onChange={(e) =>
                          handleCompanyInfoChange('brokerageAffiliation', e.target.value)
                        }
                        placeholder="Brokerage Affiliation"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <label>Assets Under Management</label>
                      <StyledInput
                        value={formData.companyInfo.aum}
                        onChange={(e) =>
                          handleCompanyInfoChange('aum', e.target.value)
                        }
                        placeholder="AUM (e.g., $100M)"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <label>Email Address</label>
                      <StyledInput
                        value={formData.companyInfo.emailAddress}
                        onChange={(e) =>
                          handleCompanyInfoChange('emailAddress', e.target.value)
                        }
                        placeholder="Email Address"
                      />
                    </InputWrapper>

                    <InputWrapper>
                      <label>Footer Text</label>
                      <StyledInput.TextArea
                        value={formData.footerText}
                        onChange={(e) =>
                          handleChange('footerText', e.target.value)
                        }
                        placeholder="Footer Text"
                        rows={3}
                      />
                    </InputWrapper>
                  </Space>
                </div>
              </div>
            ),
          },
        ]
      : [
          {
            title: 'Flyer Details',
            content: (
              <div className="space-y-6">
                <div>
                  <SectionSubtitle>
                    {renderLabel(
                      'Color Scheme',
                      'Choose the primary color scheme for your flyer'
                    )}
                  </SectionSubtitle>
                  <InputWrapper>
                    <ColorPicker
                      value={formData.colorScheme}
                      onChange={(color) => handleChange('colorScheme', color.toHexString())}
                      presets={[
                        {
                          label: 'Recommended',
                          colors: [
                            '#4a90e2', // Blue
                            '#50C878', // Green
                            '#FF6B6B', // Red
                            '#9B59B6', // Purple
                            '#34495E', // Dark Blue
                            '#2ECC71', // Emerald
                            '#E74C3C', // Bright Red
                            '#8E44AD', // Deep Purple
                          ],
                        },
                      ]}
                    />
                  </InputWrapper>
                  <div style={{ marginTop: '16px', marginBottom: '32px' }}>
                    <div
                      style={{
                        width: '100%',
                        height: '60px',
                        backgroundColor: formData.colorScheme,
                        borderRadius: '8px',
                        marginBottom: '8px',
                        border: '1px solid #e6e6e6',
                      }}
                    />
                    <Text type="secondary">Preview of selected color</Text>
                  </div>
                </div>

                <div>
                  <SectionSubtitle>
                    {renderLabel(
                      'Flyer Information',
                      'Customize the information that will appear on your flyer'
                    )}
                  </SectionSubtitle>

                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    {/* Company Information Section */}
                    <div>
                      <Title level={5}>Company Information</Title>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <InputWrapper>
                          <label>Company Name</label>
                          <StyledInput
                            value={formData.companyInfo.name}
                            onChange={(e) => handleCompanyInfoChange('name', e.target.value)}
                            placeholder="Company Name"
                          />
                        </InputWrapper>

                        <InputWrapper>
                          <label>Company Address (each line separated by Enter)</label>
                          <StyledInput.TextArea
                            value={formData.companyInfo.address}
                            onChange={(e) => handleCompanyInfoChange('address', e.target.value)}
                            placeholder={`Street Address\nSuite Number\nCity, State ZIP`}
                            rows={3}
                          />
                        </InputWrapper>

                        <InputWrapper>
                          <label>Phone Number</label>
                          <StyledInput
                            value={formData.companyInfo.phone}
                            onChange={(e) => handleCompanyInfoChange('phone', e.target.value)}
                            placeholder="Phone Number (e.g., (800) 123-4567)"
                          />
                        </InputWrapper>

                        <InputWrapper>
                          <label>Website</label>
                          <StyledInput
                            value={formData.companyInfo.website}
                            onChange={(e) => handleCompanyInfoChange('website', e.target.value)}
                            placeholder="Website URL (e.g., www.company.com)"
                          />
                        </InputWrapper>

                        <InputWrapper>
                          <label>Assets Under Management</label>
                          <StyledInput
                            value={formData.companyInfo.aum}
                            onChange={(e) => handleCompanyInfoChange('aum', e.target.value)}
                            placeholder="AUM (e.g., $100M)"
                          />
                        </InputWrapper>
                      </Space>
                    </div>

                    {/* Advisor Information Section */}
                    <div>
                      <Title level={5}>Advisor Information</Title>
                      {formData.advisors.map((advisor, index) => (
                        <div key={index} className="mb-4 p-4 border rounded">
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <InputWrapper>
                              <label>Advisor Name</label>
                              <StyledInput
                                value={advisor.name}
                                onChange={(e) => {
                                  const newAdvisors = [...formData.advisors];
                                  newAdvisors[index] = { ...advisor, name: e.target.value };
                                  setFormData(prev => ({ ...prev, advisors: newAdvisors }));
                                }}
                                placeholder="Advisor Name"
                              />
                            </InputWrapper>

                            <InputWrapper>
                              <label>Advisor Email</label>
                              <StyledInput
                                value={advisor.contact}
                                onChange={(e) => {
                                  const newAdvisors = [...formData.advisors];
                                  newAdvisors[index] = { ...advisor, contact: e.target.value };
                                  setFormData(prev => ({ ...prev, advisors: newAdvisors }));
                                }}
                                placeholder="Advisor Email"
                              />
                            </InputWrapper>

                            <InputWrapper>
                              <label>Advisor Phone</label>
                              <StyledInput
                                value={advisor.phone}
                                onChange={(e) => {
                                  const newAdvisors = [...formData.advisors];
                                  newAdvisors[index] = { ...advisor, phone: e.target.value };
                                  setFormData(prev => ({ ...prev, advisors: newAdvisors }));
                                }}
                                placeholder="Advisor Phone"
                              />
                            </InputWrapper>

                            <InputWrapper>
                              <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={(file) => {
                                  handleImageUpload(file, 'advisor', index);
                                  return false; // Prevent automatic upload
                                }}
                              >
                                <Button icon={<UploadOutlined />}>Upload Photo</Button>
                              </Upload>
                              {advisorImages[index] && (
                                <div style={{ marginTop: '8px' }}>
                                  <img
                                    src={advisorImages[index]}
                                    alt={`Advisor ${index + 1}`}
                                    style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                  />
                                </div>
                              )}
                            </InputWrapper>
                          </Space>
                          
                          {/* Add delete button for removing advisors */}
                          <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              const newAdvisors = formData.advisors.filter((_, i) => i !== index);
                              setFormData(prev => ({ ...prev, advisors: newAdvisors }));
                              setAdvisorImages(prev => {
                                const updated = { ...prev };
                                delete updated[index];
                                return updated;
                              });
                            }}
                            style={{ marginTop: '8px' }}
                          >
                            Remove Advisor
                          </Button>
                        </div>
                      ))}
                      
                      {/* Add button for new advisors */}
                      <Button 
                        type="dashed" 
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            advisors: [...prev.advisors, { name: '', contact: '', phone: '' }]
                          }));
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%' }}
                        disabled={formData.advisors.length >= 3}
                      >
                        Add Advisor {formData.advisors.length >= 3 ? '(Max 3)' : ''}
                      </Button>
                      {formData.advisors.length < 3 && (
                        <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                          You can add {3 - formData.advisors.length} more advisor{3 - formData.advisors.length !== 1 ? 's' : ''}
                        </Text>
                      )}
                    </div>

                    {/* Company Logo Section */}
                    <div>
                      <Title level={5}>Company Logo</Title>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={(file) => {
                          handleImageUpload(file, 'logo');
                          return false; // Prevent automatic upload
                        }}
                      >
                        <Button icon={<UploadOutlined />}>Upload Logo</Button>
                      </Upload>
                      {companyLogo && (
                        <div style={{ marginTop: '8px' }}>
                          <img
                            src={companyLogo}
                            alt="Company Logo"
                            style={{ width: '200px', objectFit: 'contain' }}
                          />
                        </div>
                      )}
                    </div>
                  </Space>
                </div>
              </div>
            ),
          },
          {
            title: 'Review',
            content: (
              <div className="space-y-4">
                <SectionSubtitle>Review and Confirm Your Information</SectionSubtitle>
                <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                  <div className="flex justify-between items-center">
                    <span>Material Type: {formData.materialType}</span>
                    <StyledButton
                      icon={<EditOutlined />}
                      onClick={() => handleEdit('materialType')}
                    >
                      Edit
                    </StyledButton>
                  </div>
                  {formData.materialType === 'presentation' ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span>Template: {formData.template}</span>
                        <StyledButton
                          icon={<EditOutlined />}
                          onClick={() => handleEdit('presentationDetails')}
                        >
                          Edit
                        </StyledButton>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>
                          Service Schedule:{' '}
                          {formData.includeServiceSchedule ? 'Included' : 'Not Included'}
                        </span>
                        <StyledButton
                          icon={<EditOutlined />}
                          onClick={() => handleEdit('presentationDetails')}
                        >
                          Edit
                        </StyledButton>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Fee Schedule: {formData.feeSchedule.length} ranges</span>
                        <StyledButton
                          icon={<EditOutlined />}
                          onClick={() => handleEdit('presentationDetails')}
                        >
                          Edit
                        </StyledButton>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>References: {formData.references.length} references</span>
                        <StyledButton
                          icon={<EditOutlined />}
                          onClick={() => handleEdit('presentationDetails')}
                        >
                          Edit
                        </StyledButton>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span>Flyer Details</span>
                      <StyledButton
                        icon={<EditOutlined />}
                        onClick={() => handleEdit('presentationDetails')}
                      >
                        Edit
                      </StyledButton>
                    </div>
                  )}
                </Space>
              </div>
            ),
          },
        ]),
  ];

  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const renderMobileContent = () => {
    return (
      <MobileContainer>
        <MobileContent>
          <MobileCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <MobileStepTitle level={4}>Marketing Material Generator</MobileStepTitle>
            <MobileInputGroup>
              <MobileLabel>Client Name</MobileLabel>
              <StyledInput
                placeholder="Enter client name"
                value={formData.clientName}
                onChange={(e) => handleChange('clientName', e.target.value)}
              />
            </MobileInputGroup>
            <MobileInputGroup>
              <MobileLabel>Material Type</MobileLabel>
              <Radio.Group
                onChange={(e) => handleChange('materialType', e.target.value)}
                value={formData.materialType}
              >
                <Space direction="vertical">
                  <Radio value="presentation">Presentation</Radio>
                  <Radio value="flyer">Basic Flyer</Radio>
                </Space>
              </Radio.Group>
            </MobileInputGroup>
          </MobileCard>

          {formData.materialType === 'presentation' && (
            <MobileCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <MobileStepTitle level={4}>Presentation Details</MobileStepTitle>
              <MobileInputGroup>
                <MobileLabel>Template</MobileLabel>
                <StyledSelect
                  value={formData.template}
                  onChange={(value) => handleChange('template', value)}
                >
                  {templates.map((template) => (
                    <Option key={template.value} value={template.value}>
                      {template.label}
                    </Option>
                  ))}
                </StyledSelect>
              </MobileInputGroup>

              <MobileCollapse expandIconPosition="right">
                <Panel header="Service Schedule" key="1">
                  <Checkbox
                    checked={formData.includeServiceSchedule}
                    onChange={(e) =>
                      handleChange('includeServiceSchedule', e.target.checked)
                    }
                  >
                    Include Service Schedule
                  </Checkbox>
                  {formData.includeServiceSchedule && (
                    <Space
                      direction="vertical"
                      style={{ width: '100%', marginTop: 16 }}
                    >
                      <StyledInput
                        placeholder="Number of Investment Reviews"
                        type="number"
                        value={formData.numInvestmentReviews}
                        onChange={(e) =>
                          handleChange('numInvestmentReviews', e.target.value)
                        }
                      />
                      <StyledInput
                        placeholder="Number of Investment Benchmark Reports"
                        type="number"
                        value={formData.numInvestmentBenchmarkReports}
                        onChange={(e) =>
                          handleChange(
                            'numInvestmentBenchmarkReports',
                            e.target.value
                          )
                        }
                      />
                      <StyledInput
                        placeholder="Number of Education Events"
                        type="number"
                        value={formData.numEducationEvents}
                        onChange={(e) =>
                          handleChange('numEducationEvents', e.target.value)
                        }
                      />
                      <StyledInput
                        placeholder="Number of Plan Reviews"
                        type="number"
                        value={formData.numPlanReviews}
                        onChange={(e) =>
                          handleChange('numPlanReviews', e.target.value)
                        }
                      />
                    </Space>
                  )}
                </Panel>
                <Panel header="Fee Schedule" key="2">
                  <Checkbox
                    checked={formData.feeSchedule.length > 0}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setFormData((prev) => ({
                          ...prev,
                          feeSchedule: [],
                        }));
                      } else if (formData.feeSchedule.length === 0) {
                        setFormData((prev) => ({
                          ...prev,
                          feeSchedule: [
                            { range: '', fee: '' },
                            { range: '', fee: '' },
                            { range: '', fee: '' },
                          ],
                        }));
                      }
                    }}
                  >
                    Include Fee Schedule
                  </Checkbox>
                  {formData.feeSchedule.length > 0 && (
                    <Space
                      direction="vertical"
                      style={{ width: '100%', marginTop: 16 }}
                    >
                      {formData.feeSchedule.map((fee, index) => (
                        <div
                          key={index}
                          className="flex space-x-2 items-start"
                        >
                          <InputWrapper className="flex-1">
                            <StyledInput
                              placeholder={`Asset Range ${index + 1}`}
                              value={fee.range}
                              onChange={(e) =>
                                handleFeeScheduleChange(
                                  index,
                                  'range',
                                  e.target.value
                                )
                              }
                              style={{ marginBottom: 8 }}
                            />
                          </InputWrapper>
                          <div className="flex-1 flex items-start space-x-2">
                            <InputWrapper className="flex-grow">
                              <StyledInput
                                placeholder={`Fee for Range ${index + 1}`}
                                value={fee.fee}
                                onChange={(e) =>
                                  handleFeeScheduleChange(
                                    index,
                                    'fee',
                                    e.target.value
                                  )
                                }
                              />
                            </InputWrapper>
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteFeeSchedule(index)}
                              danger
                            />
                          </div>
                        </div>
                      ))}
                      <MobileButton
                        onClick={handleAddFeeSchedule}
                        icon={<PlusOutlined />}
                      >
                        Add Range
                      </MobileButton>
                    </Space>
                  )}
                </Panel>
                <Panel header="Client Reference" key="3">
                  <Checkbox
                    checked={formData.references.length > 0}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setFormData((prev) => ({
                          ...prev,
                          references: [],
                        }));
                      } else if (formData.references.length === 0) {
                        setFormData((prev) => ({
                          ...prev,
                          references: [
                            {
                              company: '',
                              employees: '',
                              contactNumber: '',
                              contactName: '',
                              contactPosition: '',
                            },
                            {
                              company: '',
                              employees: '',
                              contactNumber: '',
                              contactName: '',
                              contactPosition: '',
                            },
                          ],
                        }));
                      }
                    }}
                  >
                    Include Reference
                  </Checkbox>
                  {formData.references.length > 0 && (
                    <Space
                      direction="vertical"
                      style={{ width: '100%', marginTop: 16 }}
                    >
                      {formData.references.map(
                        (reference, referenceIndex) => (
                          <div key={referenceIndex} className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                              <Text strong>
                                Reference {referenceIndex + 1}
                              </Text>
                              <Button
                                icon={<MinusOutlined />}
                                onClick={() =>
                                  handleDeleteReference(referenceIndex)
                                }
                                danger
                              />
                            </div>
                            <Space
                              direction="vertical"
                              style={{ width: '100%' }}
                            >
                              <StyledInput
                                placeholder="Company Name"
                                value={reference.company}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'company',
                                    e.target.value
                                  )
                                }
                              />
                              <StyledInput
                                placeholder="Number of Employees"
                                type="number"
                                value={reference.employees}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'employees',
                                    e.target.value
                                  )
                                }
                              />
                              <StyledInput
                                placeholder="Contact Number"
                                value={reference.contactNumber}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'contactNumber',
                                    e.target.value
                                  )
                                }
                              />
                              <StyledInput
                                placeholder="Contact Name"
                                value={reference.contactName}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'contactName',
                                    e.target.value
                                  )
                                }
                              />
                              <StyledInput
                                placeholder="Contact Position"
                                value={reference.contactPosition}
                                onChange={(e) =>
                                  handleReferenceChange(
                                    referenceIndex,
                                    'contactPosition',
                                    e.target.value
                                  )
                                }
                              />
                            </Space>
                          </div>
                        )
                      )}
                      <MobileButton
                        onClick={handleAddReference}
                        type="dashed"
                        icon={<PlusOutlined />}
                      >
                        Add Reference
                      </MobileButton>
                    </Space>
                  )}
                </Panel>
              </MobileCollapse>
            </MobileCard>
          )}
          {formData.materialType === 'flyer' && (
            <MobileCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <MobileStepTitle level={4}>Flyer Details</MobileStepTitle>
              <MobileInputGroup>
                <MobileLabel>Color Scheme</MobileLabel>
                <ColorPicker
                  value={formData.colorScheme}
                  onChange={(color) =>
                    handleChange('colorScheme', color.toHexString())
                  }
                  presets={[
                    {
                      label: 'Recommended',
                      colors: [
                        '#4a90e2', // Blue
                        '#50C878', // Green
                        '#FF6B6B', // Red
                        '#9B59B6', // Purple
                        '#34495E', // Dark Blue
                        '#2ECC71', // Emerald
                        '#E74C3C', // Bright Red
                        '#8E44AD', // Deep Purple
                      ],
                    },
                  ]}
                />
                <div style={{ marginTop: '16px', marginBottom: '32px' }}>
                  <div
                    style={{
                      width: '100%',
                      height: '60px',
                      backgroundColor: formData.colorScheme,
                      borderRadius: '8px',
                      marginBottom: '8px',
                      border: '1px solid #e6e6e6',
                    }}
                  />
                  <Text type="secondary">Preview of selected color</Text>
                </div>
              </MobileInputGroup>
            </MobileCard>
          )}

          <MobileCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <MobileButton
              type="primary"
              onClick={handleGenerateMarketingMaterial}
              loading={loading}
              icon={
                <PresentationChartBarIcon className="h-5 w-5 mr-2" />
              }
            >
              Generate{' '}
              {formData.materialType === 'presentation'
                ? 'PowerPoint'
                : 'Flyer'}
            </MobileButton>
            <MobileButton onClick={handleSave}>
              Save for Future Use
            </MobileButton>
          </MobileCard>
        </MobileContent>
      </MobileContainer>
    );
  };

  return (
    <>
      {isMobile ? (
        renderMobileContent()
      ) : (
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#4a90e2',
              borderRadius: 10,
              fontFamily: "'Inter', sans-serif",
            },
          }}
        >
          <Row
            justify="center"
            align="middle"
            className={`min-h-screen ${
              isMobile ? 'p-0' : 'py-8'
            }`}
          >
            <Col xs={24} sm={24} md={22} lg={20} xl={18}>
              <StyledCard
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className={`w-full mx-auto ${
                  isMobile
                    ? 'space-y-4'
                    : 'space-y-6 sm:space-y-8 md:space-y-10'
                }`}
              >
                <motion.div variants={itemVariants}>
                  <SectionTitle>Marketing Materials</SectionTitle>
                </motion.div>

                {!isMobile && (
                  <StyledSteps
                    current={currentStep}
                    onChange={handleStepChange}
                    responsive={false}
                    className="custom-steps"
                  >
                    {steps.map((item) => (
                      <Step key={item.title} title={item.title} />
                    ))}
                  </StyledSteps>
                )}

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                    className="w-full"
                  >
                    <StyledCard>
                      {isMobile && (
                        <Title level={4} className="mb-4">
                          {steps[currentStep].title}
                        </Title>
                      )}
                      {steps[currentStep] && steps[currentStep].content}
                    </StyledCard>
                  </motion.div>
                </AnimatePresence>

                <div
                  className={`steps-action ${
                    isMobile
                      ? 'fixed bottom-0 left-0 right-0 p-4 bg-white'
                      : 'mt-6'
                  }`}
                >
                  <Space
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    style={{ width: '100%' }}
                    size={isMobile ? 'large' : 'middle'}
                  >
                    {currentStep > 0 && (
                      <StyledButton onClick={() => prev()}>
                        Previous
                      </StyledButton>
                    )}
                    {currentStep < steps.length - 1 && (
                      <StyledButton
                        type="primary"
                        onClick={() => next()}
                        disabled={!isStepValid(currentStep)}
                      >
                        Next
                      </StyledButton>
                    )}
                    {currentStep === steps.length - 1 && (
                      <>
                        <StyledButton
                          type="primary"
                          onClick={handleGenerateMarketingMaterial}
                          loading={loading}
                          icon={
                            <PresentationChartBarIcon className="h-5 w-5 mr-2" />
                          }
                        >
                          Generate {' '}
                          {formData.materialType === 'presentation'
                            ? 'PowerPoint'
                            : 'Flyer'}
                        </StyledButton>
                        <StyledButton
                          onClick={handleSave}
                          icon={<BookmarkIcon className="h-5 w-5 mr-2" />}
                        >
                          Save for Future Use
                        </StyledButton>
                      </>
                    )}
                  </Space>
                </div>
              </StyledCard>
            </Col>
          </Row>
        </ConfigProvider>
      )}
    </>
  );
};

export default MarketingTemplateForm;

